import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Timeline,
  Spin,
  message,
  Typography,
  Space,
  Tag,
  Button,
  Empty,
  Row,
  Col,
} from 'antd';
import {
  BellOutlined,
  FileTextOutlined,
  ToolOutlined,
  GiftOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import PublicLayout from '../components/landing/PublicLayout';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { customerPortalApi } from '../services/apiInstances';

const { Title, Text } = Typography;

interface Notification {
  id: number;
  type: 'communication' | 'estimate' | 'work_order' | 'reward';
  title: string;
  description: string;
  category: string;
  date: string;
  relatedId?: number;
}

const CustomerNotifications: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useCustomerAuth();
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const loadNotifications = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await customerPortalApi.get(
        '/customer-portal/notifications?limit=100'
      );

      setNotifications(response.data);
    } catch (error) {
      console.error('Error loading notifications:', error);
      message.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/customer/login');
      return;
    }
    loadNotifications();
  }, [isAuthenticated, navigate, loadNotifications]);

  const getIcon = (type: string) => {
    const iconMap: { [key: string]: React.ReactNode } = {
      estimate: <FileTextOutlined style={{ color: '#1890ff', fontSize: 18 }} />,
      work_order: <ToolOutlined style={{ color: '#722ed1', fontSize: 18 }} />,
      reward: <GiftOutlined style={{ color: '#faad14', fontSize: 18 }} />,
      communication: <BellOutlined style={{ color: '#52c41a', fontSize: 18 }} />,
    };
    return iconMap[type] || <BellOutlined />;
  };

  const getStatusColor = (category: string) => {
    const colorMap: { [key: string]: string } = {
      draft: 'processing',
      pending: 'processing',
      in_progress: 'processing',
      completed: 'success',
      archived: 'default',
      earned: 'success',
      redeemed: 'orange',
      adjusted: 'blue',
      estimate: 'blue',
      work_order: 'purple',
    };
    return colorMap[category] || 'default';
  };

  const handleNavigateToItem = (notification: Notification) => {
    // NOTE: per-item detail routes (/customer/estimates/:id, /customer/work-orders/:id)
    // are not registered in App.tsx, so deep-linking by id dead-ends on a blank page.
    // Route to the registered list views instead until detail routes are added (see fix notes / shared PR).
    if (notification.type === 'estimate') {
      navigate('/customer/estimates');
    } else if (notification.type === 'work_order') {
      navigate('/customer/history');
    }
  };

  if (loading) {
    return (
      <PublicLayout>
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <Spin size="large" />
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(180deg, #f0f2f5 0%, #ffffff 100%)',
          padding: '40px 20px',
        }}
      >
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          {/* Header */}
          <Card style={{ marginBottom: 24 }}>
            <Space direction="vertical" size={0}>
              <Title level={3} style={{ margin: 0 }}>
                <BellOutlined /> Notifications & Updates
              </Title>
              <Text type="secondary">
                {notifications.length} updates from your activity
              </Text>
            </Space>
          </Card>

          {/* Notifications Timeline */}
          <Card>
            {notifications.length > 0 ? (
              <Timeline
                items={notifications.map((notif) => ({
                  dot: getIcon(notif.type),
                  children: (
                    <div
                      style={{
                        background: '#fafafa',
                        padding: '16px',
                        borderRadius: 8,
                        marginBottom: 12,
                      }}
                    >
                      <Row justify="space-between" align="middle" gutter={16}>
                        <Col flex="auto">
                          <Space direction="vertical" style={{ width: '100%' }} size="small">
                            <Space>
                              <Text strong>{notif.title}</Text>
                              <Tag color={getStatusColor(notif.category)}>
                                {notif.category?.replace(/_/g, ' ')}
                              </Tag>
                            </Space>
                            <Text type="secondary">{notif.description}</Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {dayjs(notif.date).format('MMMM D, YYYY • h:mm A')}
                            </Text>
                          </Space>
                        </Col>
                        {(notif.type === 'estimate' || notif.type === 'work_order') &&
                          notif.relatedId && (
                            <Col>
                              <Button
                                type="primary"
                                size="small"
                                onClick={() => handleNavigateToItem(notif)}
                              >
                                View Details
                              </Button>
                            </Col>
                          )}
                      </Row>
                    </div>
                  ),
                }))}
              />
            ) : (
              <Empty
                description="No notifications yet"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}
          </Card>

          {/* Info Card */}
          <Card style={{ marginTop: 24, background: '#f0f2f5' }}>
            <Space direction="vertical" size="small">
              <Text strong>About Your Notifications</Text>
              <ul style={{ margin: '8px 0', paddingLeft: 20, color: 'rgba(0, 0, 0, 0.45)', fontSize: 12 }}>
                <li>Estimate notifications alert you when new estimates are ready</li>
                <li>Work order updates track the progress of your vehicle service</li>
                <li>Reward notifications track your points and bonuses earned</li>
                <li>Communication logs show important messages from our team</li>
              </ul>
              <Button
                type="link"
                onClick={() => navigate('/customer/preferences')}
                style={{ padding: 0 }}
              >
                Manage notification preferences →
              </Button>
            </Space>
          </Card>
        </div>
      </div>
    </PublicLayout>
  );
};

export default CustomerNotifications;
